import posthog from 'posthog-js';
import ReactGA from 'react-ga4';
import { Location } from 'react-router';

import Language from '../helpers/Language';
import { SaveNoSlotParams } from '../interfaces/AvailableNoSlots';
import { Urls } from '../interfaces/Calendar';
import { EnabledLanguage } from '../interfaces/Language';
import { SignatureUpdateParams } from '../interfaces/Signature';
import { GroupedSlot, TopSlot } from '../interfaces/TopSlot';

interface Dimensions {
  appointmentId: string; // appointment id used by GA to identify sessions
  hashId: string; // hash id used by posthog to identify sessions
  sessionId: number; // session id
  timestamp: number; // timestamp
}

let isGaInitialized = false;
let initialSource: string | null = null;
let initialReminderVersion: string | null = null;
const dimensions: Partial<Dimensions> = {};

const updateDimensions = <T extends keyof Dimensions>(key: T, value: Dimensions[T]) => {
  dimensions[key] = value;
};

const setAppointmentIdDimension = (id: string | undefined): void => {
  if (id) updateDimensions('appointmentId', id);
};

const setTimestampDimension = (): void => {
  updateDimensions('timestamp', Date.now());
};

const setSessionIdDimension = (): void => {
  if (!dimensions.sessionId) updateDimensions('sessionId', Date.now());
};

const setHashIdDimension = (id: string): void => {
  updateDimensions('hashId', id);
};

export const initGA = (analyticsId: string): void => {
  ReactGA.initialize(analyticsId, {
    gaOptions: {
      // Initial values for dimensions so they are logged on every event. Dimensions are being overwritten during events
      dimension1: dimensions.appointmentId || 'none',
      dimension2: dimensions.timestamp || Date.now(),
      dimension3: dimensions.sessionId || Date.now(),
    },
  });
  isGaInitialized = true;
};

export const sendAnalyticsEvent = (
  category: string,
  eventName: string,
  data: unknown = '',
  nonInteraction = false,
  target: 'both' | 'ga' | 'posthog' = 'both'
): void => {
  setTimestampDimension();

  if (target === 'posthog' || target === 'both') {
    posthog.capture(eventName, {
      eventCategory: category,
      data: data ? data : undefined,
      nonInteraction,
      hashId: dimensions.hashId,
      manualTimestamp: dimensions.timestamp || Date.now(),
      manualSessionId: dimensions.sessionId || Date.now(),
    });
  }

  if ((target === 'ga' || target === 'both') && isGaInitialized) {
    ReactGA.event(eventName, {
      event_category: category,
      event_label: typeof data === 'string' ? data : JSON.stringify(data),
      nonInteraction,
      dimension1: dimensions.appointmentId,
      dimension2: dimensions.timestamp,
      dimension3: dimensions.sessionId,
    });
  }
};

export const saveInitialSource = (searchString: string): void => {
  const urlParams = new URLSearchParams(searchString);
  initialSource = urlParams.get('s');
  initialReminderVersion = urlParams.get('r');
};

export const sendPageView = (location: Location, prevLocation?: Location): void => {
  if (!isGaInitialized) {
    return;
  }

  setTimestampDimension();

  // https://github.com/codler/react-ga4/blob/master/src/ga4.js#L292
  ReactGA._gaCommandSendPageview(
    location.pathname,
    {
      dimension1: dimensions.appointmentId,
      dimension2: dimensions.timestamp,
      dimension3: dimensions.sessionId,
      title: typeof document !== 'undefined' ? document.title : '',
      location: typeof window !== 'undefined' ? window.location.href : '',
      page_referrer: prevLocation?.pathname || ''
    }
  );
};

export const sendSource = (target: 'both' | 'ga' | 'posthog'): void => {
  if (!initialSource) {
    initialSource = 'direct';
  }

  if (!initialReminderVersion) {
    initialReminderVersion = '0';
  }

  sendAnalyticsEvent('navigation', 'source', { source: initialSource, reminder: initialReminderVersion }, true, target);
};

const addLanguage = <T extends object>(params: T) => {
  const language: EnabledLanguage = Language.getLanguage();

  return { ...params, l: language };
};

export const identifyPosthog = (id: string): void => {
  setHashIdDimension(id);
  setSessionIdDimension();
  posthog.identify(id);
  sendSource('posthog');
};

export const setAppointmentId = (appointmentId: string): void => {
  setAppointmentIdDimension(appointmentId);
  setSessionIdDimension();
  // since we do not know if we can mutate default events to include appointmentId
  // after initialisation we defer until we first get the appointmentId
  // this has downside of GA not initializing if there is no appointment right now
  initGA(import.meta.env.VITE_GOOGLE_ANALYTICS);
  sendSource('ga');
};

export const setNextStepGA = (step: string): void => {
  sendAnalyticsEvent('scheduling', 'next-step', step);
};

export const sendReloadOnSchedulingError = (): void => {
  sendAnalyticsEvent('scheduling', 'reload-on-scheduling-error');
};

export const selectedTopSlotIndexGA = (selectedTopSlotIndex: number): void => {
  sendAnalyticsEvent('scheduling', 'selected-topslot-index', selectedTopSlotIndex);
};

export const sendAnalyticsLanguageSwitch = (selectedLanguage: EnabledLanguage): void => {
  sendAnalyticsEvent('language', 'update-selected-langauge', selectedLanguage);
};

const formatTimeslot = (timeslot: TopSlot) => ({
  g: Number(timeslot.grade.toString().substring(0, 5)),
  s: timeslot.start,
  e: timeslot.end,
});

export const sendAnalyticsSlotsReceived = (slots: GroupedSlot[]): void => {
  if (slots.length) {
    sendAnalyticsEvent('scheduling', 'slots-received', slots, false, 'posthog');
    slots.forEach(groupedSlot => {
      if (Array.isArray(groupedSlot.timeslots) && groupedSlot.timeslots.length) {
        groupedSlot.timeslots.forEach(timeslot => {
          const formattedTimeslot = formatTimeslot(timeslot);

          if (Number.isFinite(formattedTimeslot.g)) {
            sendAnalyticsEvent(
              'scheduling',
              'slots-received',
              { d: groupedSlot.date, t: formatTimeslot(timeslot) },
              false,
              'ga'
            );
          } else {
            sendAnalyticsEvent('debug', 'slots-grade-faulty', { faultyValue: timeslot.grade }, false);
          }
        });
      } else {
        sendAnalyticsEvent('scheduling', 'slots-received', undefined, false, 'ga');
      }
    });
  } else {
    sendAnalyticsEvent('scheduling', 'slots-received');
  }
};

export const sendAnalyticsDateSelected = (date: string): void => {
  sendAnalyticsEvent('scheduling', 'selected-date', date);
};

export const sendDatebarScroll = (direction: 'left' | 'right'): void => {
  sendAnalyticsEvent('scheduling', 'datebar-scroll', direction);
};

export const sendDatebarLoadMore = (): void => {
  sendAnalyticsEvent('scheduling', 'datebar-load-more');
};

export const sendAnalyticsNoSlots = (params: SaveNoSlotParams): void => {
  sendAnalyticsEvent('scheduling', 'save-no-slots', { ...addLanguage(params) });
};

export const successfullyBookedAppointmentGA = (topSlot: TopSlot): void => {
  sendAnalyticsEvent('scheduling', 'successfully-booked', { ...addLanguage(formatTimeslot(topSlot)) });
};

export const sendAnalyticsNoSlotsWithError = (requestsWithError: number): void => {
  sendAnalyticsEvent('scheduling', 'get-no-slots-with-error', { requestsWithError });
};

export const sendChatRequested = (page: string): void => {
  sendAnalyticsEvent('support', 'chat-requested', { page });
};

export const sendFAQstring = (value: string): void => {
  sendAnalyticsEvent('support', 'faq-search', { value });
};

export const sendSubmitPersonalDetailsClicked = (): void => {
  sendAnalyticsEvent('customer', 'save-details-clicked');
};

export const sendRescheduleClicked = (): void => {
  sendAnalyticsEvent('scheduling', 'reschedule-appointment-clicked');
};

export const sendCancelAppointmentClicked = (): void => {
  sendAnalyticsEvent('scheduling', 'cancel-appointment-clicked');
};

export const sendConfirmShown24Hours = (): void => {
  sendAnalyticsEvent('navigation', 'confirm-shown-24-hours');
};

export const sendCalendarOpened = (): void => {
  sendAnalyticsEvent('scheduling', 'add-to-calendar-open');
};

export const sendCalendarSelected = (value: keyof Urls): void => {
  sendAnalyticsEvent('scheduling', 'add-to-calendar-select', value);
};

export const sendDifferenceInDaysSelectedInCalendar = (days: number): void => {
  sendAnalyticsEvent('scheduling', 'difference-days-requested-slots', days);
};

export const sendSignatureDetailsUpdated = (
  params: { imageSize?: number } & Pick<SignatureUpdateParams, 'customerSigned'>
): void => {
  sendAnalyticsEvent('signature', 'report-signed', { ...params });
};

export const sendSalesforceChatInitialized = (): void => {
  sendAnalyticsEvent('chat-script-init', 'salesforce-chat-initialized');
};

export const sendSalesforceChatIntialisationError = (error: Event | string): void => {
  sendAnalyticsEvent('chat-script-init', 'salesforce-chat-intialisation-error', { error });
};

export const sendRetry = (retry: number | undefined, url: string): void => {
  sendAnalyticsEvent('debug', 'retry-request', { retry, url });
};

export const sendException = (error: Error): void => {
  sendAnalyticsEvent('exception', 'error', {
    fatal: true,
    description: error.toString()
  });
};
